import React, { useMemo, useRef, useState, useId } from 'react';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

// Type-ahead replacement for the destination <Select>: filters as you type
// (prefix matches ranked above substring matches), ArrowUp/ArrowDown move the
// highlight, Enter/tap selects (Enter is swallowed so the surrounding quote
// form doesn't submit mid-pick), Escape closes. enterKeyHint surfaces a
// "Go" key on mobile keyboards, which fires the same Enter handler.
const CountryCombobox = ({
  countries = [],
  value,
  onChange,
  placeholder = 'Select destination',
  loading = false,
  id,
  className,
}) => {
  const listboxId = useId();
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);

  const items = useMemo(
    () =>
      countries
        .map((c) => ({
          label: c.name || c.countryName || '',
          code: c.code || c.countryCode || '',
        }))
        .filter((i) => i.label && i.code),
    [countries]
  );

  const selected = useMemo(
    () => items.find((i) => i.code === value) || null,
    [items, value]
  );

  // Most-shipped lanes out of India, surfaced above the A-Z list
  const POPULAR = useMemo(
    () => [
      'united states of america',
      'united kingdom',
      'canada',
      'australia',
      'united arab emirates',
    ],
    []
  );

  const popularItems = useMemo(() => {
    const byName = new Map(items.map((i) => [i.label.toLowerCase(), i]));
    return POPULAR.map((n) => byName.get(n)).filter(Boolean);
  }, [items, POPULAR]);

  const popularCodes = useMemo(
    () => new Set(popularItems.map((i) => i.code)),
    [popularItems]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // No search: popular first, then everyone else alphabetically
    if (!q && popularItems.length) {
      return [...popularItems, ...items.filter((i) => !popularCodes.has(i.code))];
    }
    if (!q) return items;
    const starts = [];
    const contains = [];
    for (const item of items) {
      const label = item.label.toLowerCase();
      if (label.startsWith(q)) starts.push(item);
      else if (label.includes(q)) contains.push(item);
    }
    return [...starts, ...contains];
  }, [items, query]);

  const activeIndex = Math.min(highlight, Math.max(filtered.length - 1, 0));

  const scrollTo = (idx) => {
    requestAnimationFrame(() => {
      const el = listRef.current?.querySelector(`[data-index="${idx}"]`);
      if (el) el.scrollIntoView({ block: 'nearest' });
    });
  };

  const openList = () => {
    const idx = selected
      ? Math.max(0, items.findIndex((i) => i.code === selected.code))
      : 0;
    setQuery('');
    setHighlight(idx);
    setOpen(true);
    scrollTo(idx);
  };

  const closeList = () => {
    setOpen(false);
    setQuery('');
  };

  const select = (item) => {
    if (onChange) onChange(item.code);
    closeList();
    if (inputRef.current) inputRef.current.blur();
  };

  const move = (delta) => {
    if (!filtered.length) return;
    const next = (activeIndex + delta + filtered.length) % filtered.length;
    setHighlight(next);
    scrollTo(next);
  };

  const handleKeyDown = (e) => {
    if (!open) {
      // Enter on a closed combobox falls through to the form (submit);
      // arrows re-open the list.
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(-1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setHighlight(0);
      scrollTo(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setHighlight(filtered.length - 1);
      scrollTo(filtered.length - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered.length) select(filtered[activeIndex]);
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      closeList();
    } else if (e.key === 'Tab') {
      closeList();
    }
  };

  return (
    <div className={cn('relative', className)}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          open && filtered.length ? `${listboxId}-${activeIndex}` : undefined
        }
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="go"
        placeholder={selected ? selected.label : placeholder}
        value={open ? query : selected ? selected.label : ''}
        onFocus={openList}
        onBlur={closeList}
        onChange={(e) => {
          setQuery(e.target.value);
          setHighlight(0);
          if (!open) setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        className={cn(
          'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 pr-8 text-base shadow-sm transition-colors',
          'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm',
          !open && selected && 'text-foreground'
        )}
      />
      <ChevronDown
        className={cn(
          'pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground transition-transform',
          open && 'rotate-180'
        )}
      />
      {open && (
        <div
          ref={listRef}
          id={listboxId}
          role="listbox"
          // preventDefault keeps the input focused so onClick fires before blur
          onMouseDown={(e) => e.preventDefault()}
          // Floats over the quote card rather than being clipped by it
          className="absolute left-0 top-full z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-lg"
        >
          {loading ? (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading destinations...
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No destinations match &quot;{query}&quot;
            </div>
          ) : (
            filtered.map((item, idx) => {
              const showingGroups = !query.trim() && popularItems.length > 0;
              const isFirstPopular = showingGroups && idx === 0;
              const isFirstOther = showingGroups && idx === popularItems.length;
              return (
              <React.Fragment key={item.code}>
              {isFirstPopular && (
                <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Popular destinations
                </div>
              )}
              {isFirstOther && (
                <div className="mt-1 border-t px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  All destinations
                </div>
              )}
              <div
                id={`${listboxId}-${idx}`}
                data-index={idx}
                role="option"
                aria-selected={item.code === value}
                onClick={() => select(item)}
                onMouseMove={() => setHighlight(idx)}
                className={cn(
                  'flex cursor-pointer items-center justify-between px-3 py-2 text-sm',
                  idx === activeIndex && 'bg-accent text-accent-foreground',
                  item.code === value && 'font-medium'
                )}
              >
                <span className="truncate">{item.label}</span>
                {item.code === value && (
                  <Check className="h-4 w-4 shrink-0 text-[#F17221]" />
                )}
              </div>
              </React.Fragment>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export { CountryCombobox };
